import React, { useState, useEffect, useRef } from 'react';
import { stagger } from '../lib/stagger';
import { Pill } from '../lib/ui';
import { loadLeaflet, tileLayer } from '../lib/leafletMap';

// Also drawn (faintly) on the Leaflets page's zone editor, as a guide.
export const POSTCODE_DATA = [
  // SS5 core
  { sector: 'SS5 4', label: 'Hockley & Hawkwell',          lat: 51.597, lng: 0.657, priority: 5, notes: 'Your heartland. High owner-occupier detached housing, professional households. Top priority.', delivered: 842 },
  { sector: 'SS5 5', label: 'Hockley South',                lat: 51.585, lng: 0.663, priority: 4, notes: 'Mix of semis and detached. Good coverage already achieved.', delivered: 614 },
  { sector: 'SS5 6', label: 'Hullbridge',                   lat: 51.614, lng: 0.621, priority: 4, notes: 'River-facing village, high homeownership, aspirational households.', delivered: 0 },
  // SS4
  { sector: 'SS4 1', label: 'Rochford Town',                lat: 51.579, lng: 0.709, priority: 4, notes: 'Market town with professional commuter households. Strong prospect.', delivered: 310 },
  { sector: 'SS4 2', label: 'Stambridge & rural east',      lat: 51.569, lng: 0.742, priority: 2, notes: 'Scattered rural. Low household density - high effort for low return.', delivered: 0 },
  { sector: 'SS4 3', label: 'Ashingdon & Canewdon',         lat: 51.608, lng: 0.692, priority: 5, notes: 'Owner-occupied estates, higher earners. Excellent demographic match.', delivered: 487 },
  // SS6 Rayleigh
  { sector: 'SS6 6', label: 'Rayleigh NW',                  lat: 51.614, lng: 0.589, priority: 3, notes: 'London Road corridor, commuter belt. Decent profile but thinner.', delivered: 0 },
  { sector: 'SS6 7', label: 'Rayleigh North',               lat: 51.604, lng: 0.601, priority: 4, notes: 'Affluent Rayleigh edge. Strong homeownership and professional density.', delivered: 0 },
  { sector: 'SS6 8', label: 'Rayleigh East',                lat: 51.591, lng: 0.615, priority: 4, notes: 'FitzWimarc catchment - professional families, excellent match.', delivered: 0 },
  { sector: 'SS6 9', label: 'Rayleigh South',               lat: 51.578, lng: 0.602, priority: 3, notes: 'Solid residential but more mixed. Cover after core zones.', delivered: 0 },
  // SS7 Benfleet/Thundersley
  { sector: 'SS7 1', label: 'Thundersley North',            lat: 51.570, lng: 0.567, priority: 3, notes: 'Quieter residential. Decent profile but western edge of range.', delivered: 0 },
  { sector: 'SS7 2', label: 'Benfleet South',               lat: 51.551, lng: 0.557, priority: 2, notes: 'More mixed tenure, lower affluence. Lower yield for the travel.', delivered: 0 },
  { sector: 'SS7 3', label: 'Thundersley South',            lat: 51.557, lng: 0.578, priority: 2, notes: 'Mixed, reasonable but not your core profile.', delivered: 0 },
  { sector: 'SS7 4', label: 'Hadleigh',                     lat: 51.549, lng: 0.603, priority: 2, notes: 'Lower homeownership rates. Skip unless spare capacity.', delivered: 0 },
  { sector: 'SS7 5', label: 'Daws Heath',                   lat: 51.568, lng: 0.628, priority: 4, notes: 'Leafy, detached housing belt. Strong profile - prioritise.', delivered: 0 },
  // SS9 Leigh/Eastwood
  { sector: 'SS9 4', label: 'Eastwood North',               lat: 51.561, lng: 0.651, priority: 3, notes: 'Good owner-occupier rates. Borders SS5 - decent spillover.', delivered: 0 },
  { sector: 'SS9 5', label: 'Eastwood South',               lat: 51.555, lng: 0.638, priority: 3, notes: 'Similar to SS9 4. Mid-tier prospect.', delivered: 0 },
  { sector: 'SS9 1', label: 'Leigh-on-Sea Central',         lat: 51.541, lng: 0.655, priority: 3, notes: 'Affluent in places but dense urban - flier efficiency lower.', delivered: 0 },
  // SS11 Wickford
  { sector: 'SS11 7', label: 'Wickford N & Battlesbridge',  lat: 51.622, lng: 0.529, priority: 3, notes: 'Battlesbridge has good pockets. Selective delivery worthwhile.', delivered: 0 },
  { sector: 'SS11 8', label: 'Wickford & Rawreth',          lat: 51.610, lng: 0.548, priority: 3, notes: 'Rawreth Lane corridor has good detached housing. Worth a targeted run.', delivered: 0 },
  // SS12 Wickford south
  { sector: 'SS12 0', label: 'Wickford South',              lat: 51.597, lng: 0.519, priority: 2, notes: 'More urban Wickford. Lower affluence profile vs north.', delivered: 0 },
  // CM3 South Woodham Ferrers
  { sector: 'CM3 5', label: 'S. Woodham Ferrers Central',   lat: 51.647, lng: 0.612, priority: 4, notes: 'Planned new town, high homeownership. Young professional families - strong food values match.', delivered: 0 },
  { sector: 'CM3 6', label: 'S. Woodham Ferrers East',      lat: 51.651, lng: 0.632, priority: 4, notes: 'Same profile as CM3 5. Often overlooked by local food businesses.', delivered: 0 },
  { sector: 'CM3 7', label: 'Rettendon & Battlesbridge',    lat: 51.633, lng: 0.573, priority: 3, notes: 'Rural villages - scattered but affluent households. Low density.', delivered: 0 },
  // SS2 fringe - kept but as low/skip
  { sector: 'SS2 6', label: 'Rochford Airport fringe',      lat: 51.569, lng: 0.704, priority: 2, notes: 'Mixed area near airport. Some good streets but patchy.', delivered: 0 },
];

export const PRIORITY_CONFIG = {
  5: { label: 'Prime',  color: '#15803d', bg: '#dcfce7', border: '#4ade80' },
  4: { label: 'High',   color: '#1d4ed8', bg: '#dbeafe', border: '#60a5fa' },
  3: { label: 'Medium', color: '#b45309', bg: '#fef9c3', border: '#fcd34d' },
  2: { label: 'Low',    color: '#6b7280', bg: '#f3f4f6', border: '#d1d5db' },
  1: { label: 'Skip',   color: '#dc2626', bg: '#fee2e2', border: '#fca5a5' },
};

// Tighter radii - each sector is realistically ~1–1.5km across
const PRIORITY_RADIUS = { 5: 1400, 4: 1200, 3: 1000, 2: 850, 1: 700 };

const CAPACITY = 260;

const MapView = ({ selected, onSelect }) => {
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);

  useEffect(() => {
    if (!mapRef.current || leafletMapRef.current) return;
    const L = window.L;
    if (!L) return;

    // Tight bounds: Hockley area only - no Romford, no deep Southend
    const map = L.map(mapRef.current, {
      center: [51.598, 0.632],
      zoom: 12,
      zoomControl: true,
      maxBounds: [[51.500, 0.450], [51.710, 0.830]],
      minZoom: 11,
      maxZoom: 16,
    });

    tileLayer(L).addTo(map);

    POSTCODE_DATA.forEach((pc) => {
      const cfg = PRIORITY_CONFIG[pc.priority];
      const radius = PRIORITY_RADIUS[pc.priority];

      const circle = L.circle([pc.lat, pc.lng], {
        color: cfg.border,
        fillColor: cfg.bg,
        fillOpacity: pc.priority >= 4 ? 0.52 : 0.32,
        weight: pc.priority >= 4 ? 2 : 1.2,
        radius,
      }).addTo(map);

      // Dashed delivery ring
      if (pc.delivered > 0) {
        L.circle([pc.lat, pc.lng], {
          color: '#16a34a',
          fillColor: 'transparent',
          fillOpacity: 0,
          weight: 2,
          dashArray: '5 5',
          radius: radius + 180,
        }).addTo(map);
      }

      const icon = L.divIcon({
        className: '',
        html: `<div style="
          background:${cfg.bg};
          border:1.5px solid ${cfg.border};
          color:${cfg.color};
          font-size:9px;
          font-weight:700;
          padding:1px 5px;
          border-radius:var(--r-sm);
          white-space:nowrap;
          box-shadow:var(--shadow-card);
          font-family:system-ui,sans-serif;
          pointer-events:none;
          line-height:1.5;
        ">${pc.sector}</div>`,
        iconAnchor: [20, 8],
      });

      const marker = L.marker([pc.lat, pc.lng], { icon }).addTo(map);
      const onClick = () => onSelect(pc.sector === selected ? null : pc.sector);
      circle.on('click', onClick);
      marker.on('click', onClick);
    });

    leafletMapRef.current = map;
    return () => { map.remove(); leafletMapRef.current = null; };
  }, []);

  return (
    <div ref={mapRef} style={{ width: '100%', height: '500px', borderRadius: '0 0 var(--r-xl) var(--r-xl)', overflow: 'hidden' }} />
  );
};

const PriorityBadge = ({ priority, small }) => {
  const cfg = PRIORITY_CONFIG[priority];
  return (
    <Pill bg={cfg.bg} fg={cfg.color} style={{
      border: `1px solid ${cfg.border}`, borderRadius: 'var(--r-sm)',
      fontSize: small ? '0.64rem' : '0.7rem', fontWeight: 700,
      padding: small ? '1px 6px' : '2px 8px',
    }}>{cfg.label}</Pill>
  );
};

export default function FlierTargeting() {
  const [selected, setSelected] = useState(null);
  const [filterPriority, setFilterPriority] = useState('all');
  const [leafletLoaded, setLeafletLoaded] = useState(false);
  const [sessions, setSessions] = useState(3);

  useEffect(() => {
    loadLeaflet().then(() => setLeafletLoaded(true)).catch(() => {});
  }, []);

  const selectedData = POSTCODE_DATA.find(p => p.sector === selected);
  const totalDelivered = POSTCODE_DATA.reduce((s, p) => s + p.delivered, 0);
  const primeUntouched = POSTCODE_DATA.filter(p => p.priority >= 4 && p.delivered === 0);
  const untouchedHH = primeUntouched.length * 280;
  const estSessions = Math.ceil(untouchedHH / CAPACITY);
  const plannedReach = sessions * CAPACITY;
  const filtered = filterPriority === 'all' ? POSTCODE_DATA : POSTCODE_DATA.filter(p => p.priority === parseInt(filterPriority));

  return (
    <div className="dashboard-container">
      <div style={{ padding: '0 0 10px', borderBottom: '1px solid var(--border)', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: '1.05rem', fontWeight: 700, color: 'var(--text)' }}>Flier Targeting</span>
          <span style={{ background: '#f0fdf4', color: '#15803d', border: '1px solid #86efac', fontSize: '0.65rem', fontWeight: 700, padding: '2px 7px', borderRadius: 'var(--r-sm)' }}>5-MILE RADIUS · HOCKLEY</span>
          <span style={{ background: 'var(--panel-2)', color: 'var(--text)', border: '1px solid var(--border)', fontSize: '0.65rem', fontWeight: 600, padding: '2px 7px', borderRadius: 'var(--r-sm)' }}>{POSTCODE_DATA.length} SECTORS</span>
        </div>
      </div>

      {/* <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14, marginBottom: 20 }}>
        <StatCard value={totalDelivered.toLocaleString()} label="Total delivered" sub="all campaigns to date" i={0} />
        <StatCard value={primeUntouched.length} label="Untouched prime zones" sub="priority 4–5, not yet covered" i={1} />
        <StatCard value={`~${untouchedHH.toLocaleString()}`} label="Target HHs remaining" sub="prime + high zones only" i={2} />
        <StatCard value={estSessions} label="Sessions to cover them" sub={`at ${CAPACITY} fliers/session`} i={3} />
      </div> */}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 16, marginBottom: 20 }}>
        {/* Map panel */}
        <div className="chart-container" style={{ padding: 0, overflow: 'hidden' }}>
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>Coverage map</span>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              {Object.entries(PRIORITY_CONFIG).reverse().map(([p, cfg]) => (
                <span key={p} style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: '0.68rem', color: cfg.color, fontWeight: 600 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.border, display: 'inline-block' }} />
                  {cfg.label}
                </span>
              ))}
            </div>
          </div>
          {leafletLoaded
            ? <MapView selected={selected} onSelect={setSelected} />
            : <div style={{ height: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--muted)', fontSize: '0.8rem' }}>Loading map…</div>
          }
        </div>

        {/* Detail panel */}
        <div className="chart-container" style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 14 }}>
            <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>
              {selectedData ? selectedData.label : 'Zone detail'}
            </span>
          </div>
          {selectedData ? (
            <>
              <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                <PriorityBadge priority={selectedData.priority} />
                <span style={{ fontSize: '0.72rem', color: 'var(--muted)', fontFamily: 'monospace' }}>{selectedData.sector}</span>
              </div>
              <p style={{ fontSize: '0.8rem', color: 'var(--text)', lineHeight: 1.65, margin: '0 0 14px' }}>{selectedData.notes}</p>
              <div style={{ background: 'var(--panel-2)', borderRadius: 'var(--r-lg)', padding: '10px 14px', marginBottom: 10 }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--muted)', marginBottom: 3, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Delivered so far</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: selectedData.delivered > 0 ? 'var(--text)' : 'var(--muted)' }}>
                  {selectedData.delivered > 0 ? selectedData.delivered.toLocaleString() : '-'}
                </div>
                {selectedData.delivered === 0 && <div style={{ fontSize: '0.68rem', color: '#d97706', marginTop: 1 }}>Not yet covered</div>}
              </div>
              <div style={{ background: 'var(--panel-2)', borderRadius: 'var(--r-lg)', padding: '10px 14px' }}>
                <div style={{ fontSize: '0.65rem', color: 'var(--muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Recommendation</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text)', fontWeight: 600 }}>
                  {selectedData.priority === 5 && '✓ Top priority - do this first'}
                  {selectedData.priority === 4 && '✓ High value - include in next run'}
                  {selectedData.priority === 3 && '~ Cover after prime zones'}
                  {selectedData.priority === 2 && '↓ Low yield - only if spare capacity'}
                  {selectedData.priority === 1 && '✗ Skip - wrong demographic profile'}
                </div>
              </div>
            </>
          ) : (
            <>
              <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: '0 0 16px' }}>Click any zone on the map to see targeting notes and delivery history.</p>
              <div>
                <div style={{ fontSize: '0.65rem', fontWeight: 700, color: 'var(--muted)', marginBottom: 10, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Priority key</div>
                {Object.entries(PRIORITY_CONFIG).reverse().map(([p, cfg]) => (
                  <div key={p} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, marginBottom: 9 }}>
                    <PriorityBadge priority={parseInt(p)} small />
                    <span style={{ fontSize: '0.72rem', color: 'var(--muted)', lineHeight: 1.4 }}>
                      {p === '5' && 'Owner-occupied, high income, professional'}
                      {p === '4' && 'Good homeownership, solid prospect'}
                      {p === '3' && 'Mixed - some strong households'}
                      {p === '2' && 'Lower affluence or high rental'}
                      {p === '1' && 'Wrong demographic - skip'}
                    </span>
                  </div>
                ))}
                <div style={{ marginTop: 14, fontSize: '0.68rem', color: 'var(--muted)', borderTop: '1px solid var(--border)', paddingTop: 10 }}>
                  ╌╌ Dashed green ring = zone already delivered
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Zone table */}
      <div className="chart-container" style={stagger(5)}>
        <div style={{ borderBottom: '1px solid var(--border)', paddingBottom: 10, marginBottom: 14, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)' }}>All {POSTCODE_DATA.length} zones</span>
          <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            {['all','5','4','3','2','1'].map(v => (
              <button key={v} onClick={() => setFilterPriority(v)} className={filterPriority === v ? 'btn-tab active' : 'btn-tab'} style={{ fontSize: '0.72rem', padding: '3px 10px' }}>
                {v === 'all' ? `All (${POSTCODE_DATA.length})` : `${PRIORITY_CONFIG[parseInt(v)].label} (${POSTCODE_DATA.filter(z => z.priority === parseInt(v)).length})`}
              </button>
            ))}
          </div>
        </div>
        <div className="custom-table-wrapper">
          <table className="stats-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                {['Sector','Area','Priority','Notes'].map(h => (
                  <th key={h} style={{ fontSize: '0.65rem', color: 'var(--muted)', textAlign: h === 'Delivered' ? 'right' : 'left', paddingBottom: 8, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.sort((a,b) => b.priority - a.priority).map(pc => (
                <tr key={pc.sector} onClick={() => setSelected(pc.sector === selected ? null : pc.sector)} style={{ cursor: 'pointer', background: selected === pc.sector ? 'var(--panel-2)' : 'transparent', transition: 'background 0.15s' }}>
                  <td style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--text)', padding: '7px 0', fontFamily: 'monospace' }}>{pc.sector}</td>
                  <td style={{ fontSize: '0.8rem', color: 'var(--text)', padding: '7px 8px' }}>{pc.label}</td>
                  <td style={{ padding: '7px 8px' }}><PriorityBadge priority={pc.priority} small /></td>
                  <td style={{ fontSize: '0.73rem', color: 'var(--muted)', padding: '7px 0 7px 12px', maxWidth: 280 }}>{pc.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}